import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Field, SelectField } from "./common";
import { BESOINS, STATUTS_MISSION } from "../lib/constants";
import { useData } from "../context/DataContext";

const empty = (demandeId, type) => ({
  demande_id: demandeId || "", titre: "", type: type || "Audit", statut: "en_cours",
  date_debut: format(new Date(), "yyyy-MM-dd"), montant: 0, notes: "", livrables: [{ titre: "", echeance: "", fait: false }],
});

export const MissionFormDialog = ({ open, onOpenChange, initial, demandeId, defaultType }) => {
  const { save, demandes } = useData();
  const [f, setF] = useState(empty());
  useEffect(() => { if (open) setF(initial ? { ...initial } : empty(demandeId, defaultType)); }, [open, initial, demandeId, defaultType]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const setLiv = (i, k, v) => setF((p) => ({ ...p, livrables: p.livrables.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const addLiv = () => setF((p) => ({ ...p, livrables: [...p.livrables, { titre: "", echeance: "", fait: false }] }));
  const delLiv = (i) => setF((p) => ({ ...p, livrables: p.livrables.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.demande_id || !f.titre.trim()) return toast.error("Client et intitulé sont obligatoires");
    const payload = { ...f, montant: Number(f.montant) || 0, livrables: f.livrables.filter((l) => l.titre.trim()) };
    try {
      await save("missions", payload, initial?.id);
      toast.success(initial ? "Mission mise à jour" : "Mission créée");
      onOpenChange(false);
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="mission-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{initial ? "Modifier la mission" : "Nouvelle mission"}</DialogTitle>
          <DialogDescription>Client, livrables et échéances de la mission.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Client">
            <SelectField testId="mission-demande-select" value={f.demande_id} onChange={set("demande_id")} placeholder="Choisir un client"
              options={demandes.map((d) => ({ id: d.id, label: d.entreprise }))} />
          </Field>
          <Field label="Intitulé de la mission"><Input data-testid="mission-titre-input" value={f.titre} onChange={set("titre")} placeholder="Ex. Audit organisationnel" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type"><SelectField testId="mission-type-select" value={f.type} onChange={set("type")} options={BESOINS} /></Field>
            <Field label="Statut"><SelectField testId="mission-statut-select" value={f.statut} onChange={set("statut")} options={STATUTS_MISSION} /></Field>
            <Field label="Date de début"><Input data-testid="mission-date-input" type="date" value={f.date_debut} onChange={set("date_debut")} /></Field>
            <Field label="Montant (€ HT)"><Input data-testid="mission-montant-input" type="number" min="0" value={f.montant} onChange={set("montant")} /></Field>
          </div>
          <div className="space-y-2">
            <p className="kc-eyebrow">Livrables & échéances</p>
            {f.livrables.map((l, i) => (
              <div key={l.id || i} className="flex gap-2">
                <Input data-testid={`mission-livrable-titre-${i}`} value={l.titre} onChange={(e) => setLiv(i, "titre", e.target.value)} placeholder="Livrable" />
                <Input data-testid={`mission-livrable-echeance-${i}`} type="date" className="w-44" value={l.echeance} onChange={(e) => setLiv(i, "echeance", e.target.value)} />
                <Button type="button" variant="ghost" size="icon" onClick={() => delLiv(i)} data-testid={`mission-livrable-remove-${i}`}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addLiv} data-testid="mission-add-livrable-button"><Plus className="mr-1 h-4 w-4" />Ajouter un livrable</Button>
          </div>
          <Field label="Notes"><Textarea data-testid="mission-notes-input" rows={2} value={f.notes} onChange={set("notes")} /></Field>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" className="kc-btn-primary" data-testid="mission-form-submit-button">Enregistrer</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
